import React from 'react';
import { useSelector } from 'react-redux';
import { vehicleDetailsSelectors } from '../../store';
import { SlideOut } from '@interstate/components/SlideOut';
import type { StandardFeaturesSlideoutProps } from '../../types/VehicleSectionProps';
import {
    ContentWrapper,
    SectionBlock,
    SubSectionBlock,
    SubSectionColumn,
    SectionHeader,
    EmptyState,
    SpecGrid,
    DimensionsSpecGrid,
    SpecTwoColumnLayout,
    FeaturesTwoColumnLayout,
    SpecLabel,
    SubSectionHeader,
    SlideoutTitle,
    ColorValue,
    ColorSwatch,
    Disclaimer
} from './StandardFeaturesSlideout.style';
import { VEHICLE_DATA_DISCLAIMER } from '../../constants';
import {
    FEATURE_SECTIONS_LEFT,
    FEATURE_SECTIONS_RIGHT,
    hasColorsContent,
    hasWarrantyContent,
    renderSpecRow,
    renderSpecSection,
    renderFeatureColumn,
    getSpecSections,
    getSectionVisibility,
    buildWarrantyFields
} from './StandardFeaturesSlideout.utils';

export const StandardFeaturesSlideout: React.FC<StandardFeaturesSlideoutProps> = ({ show, onHide }) => {
    const standardFeatures = useSelector(vehicleDetailsSelectors.getStandardFeatures);
    const { specifications: specs, features, dimensions } = standardFeatures ?? {};
    const { title: vehicleTitle } = useSelector(vehicleDetailsSelectors.getVehicleDetails);
    const condition = useSelector(vehicleDetailsSelectors.getCondition);
    const inServiceDate = useSelector(vehicleDetailsSelectors.getInServiceDate);
    const odometer = useSelector(vehicleDetailsSelectors.getOdometer);

    const { leftSpecSections, rightSpecSections } = getSpecSections(specs);
    const { showSpecs, showFeatures, showDimensions, showEmptyState } = getSectionVisibility(
        standardFeatures,
        condition,
        inServiceDate,
        odometer
    );

    return (
        <SlideOut
            show={show}
            onHide={onHide}
            position="right"
            header={
                <SlideoutTitle level={4} weight="bold" forwardedAs="h4">
                    {vehicleTitle || 'Standard Features'}
                </SlideoutTitle>
            }
            panelWidth="90%"
            sx={{ '.ids-SlideOut-content': { paddingBottom: '40px' } }}
            data-testid="standard-features-slideout"
        >
            <ContentWrapper data-testid="standard-features-content">
                {showEmptyState && (
                    <EmptyState size="small" weight="regular" data-testid="standard-features-empty">
                        No feature data available.
                    </EmptyState>
                )}

                {showSpecs && specs && (
                    <SectionBlock data-testid="standard-features-specifications">
                        <SectionHeader level={4} weight="bold" forwardedAs="h4">
                            Specifications
                        </SectionHeader>
                        <SpecTwoColumnLayout>
                            <SubSectionColumn>{leftSpecSections.map(renderSpecSection)}</SubSectionColumn>
                            <SubSectionColumn>
                                {rightSpecSections.map(renderSpecSection)}
                                {hasColorsContent(specs.colors) && specs.colors && (
                                    <SubSectionBlock>
                                        <SubSectionHeader level={6} weight="bold" forwardedAs="h6">
                                            Colors
                                        </SubSectionHeader>
                                        <SpecGrid>
                                            {specs.colors.exteriorOemName && (
                                                <>
                                                    <SpecLabel size="small" weight="regular" forwardedAs="span">
                                                        Exterior:
                                                    </SpecLabel>
                                                    <ColorValue
                                                        size="small"
                                                        weight="regular"
                                                        forwardedAs="span"
                                                        data-testid="color-exterior"
                                                    >
                                                        {specs.colors.exteriorRgbHex && (
                                                            <ColorSwatch $color={`#${specs.colors.exteriorRgbHex}`} />
                                                        )}
                                                        {specs.colors.exteriorOemName}
                                                        {specs.colors.exteriorNormalizedName &&
                                                            ` (${specs.colors.exteriorNormalizedName})`}
                                                    </ColorValue>
                                                </>
                                            )}
                                            {specs.colors.interiorOemName && (
                                                <>
                                                    <SpecLabel size="small" weight="regular" forwardedAs="span">
                                                        Interior:
                                                    </SpecLabel>
                                                    <ColorValue
                                                        size="small"
                                                        weight="regular"
                                                        forwardedAs="span"
                                                        data-testid="color-interior"
                                                    >
                                                        {specs.colors.interiorRgbHex && (
                                                            <ColorSwatch $color={`#${specs.colors.interiorRgbHex}`} />
                                                        )}
                                                        {specs.colors.interiorOemName}
                                                        {specs.colors.interiorNormalizedName &&
                                                            ` (${specs.colors.interiorNormalizedName})`}
                                                    </ColorValue>
                                                </>
                                            )}
                                        </SpecGrid>
                                    </SubSectionBlock>
                                )}
                                {hasWarrantyContent(specs.warranty, condition, inServiceDate, odometer) && specs.warranty && (
                                    <SubSectionBlock>
                                        <SubSectionHeader level={6} weight="bold" forwardedAs="h6">
                                            Warranty
                                        </SubSectionHeader>
                                        <SpecGrid>
                                            {buildWarrantyFields(specs.warranty).map(([label, value]) =>
                                                renderSpecRow(label, value)
                                            )}
                                        </SpecGrid>
                                    </SubSectionBlock>
                                )}
                            </SubSectionColumn>
                        </SpecTwoColumnLayout>
                    </SectionBlock>
                )}

                {showFeatures && features && (
                    <SectionBlock data-testid="standard-features-features">
                        <SectionHeader level={4} weight="bold" forwardedAs="h4">
                            Features
                        </SectionHeader>
                        <FeaturesTwoColumnLayout>
                            <SubSectionColumn>{renderFeatureColumn(FEATURE_SECTIONS_LEFT, features)}</SubSectionColumn>
                            <SubSectionColumn>{renderFeatureColumn(FEATURE_SECTIONS_RIGHT, features)}</SubSectionColumn>
                        </FeaturesTwoColumnLayout>
                    </SectionBlock>
                )}

                {showDimensions && dimensions && (
                    <SectionBlock data-testid="standard-features-dimensions">
                        <SectionHeader level={4} weight="bold" forwardedAs="h4">
                            Dimensions
                        </SectionHeader>
                        <DimensionsSpecGrid>
                            {renderSpecRow('Width', dimensions.width)}
                            {renderSpecRow('Height', dimensions.height)}
                            {renderSpecRow('Length', dimensions.length)}
                            {renderSpecRow('Wheelbase', dimensions.wheelbase)}
                        </DimensionsSpecGrid>
                    </SectionBlock>
                )}

                <Disclaimer size="xsmall" weight="regular" data-testid="standard-features-disclaimer">
                    {VEHICLE_DATA_DISCLAIMER}
                </Disclaimer>
            </ContentWrapper>
        </SlideOut>
    );
};
